import * as Automerge from "@automerge/automerge";
import type {
  WSClientOptions,
  WSDisconnectInfo,
  WSReconnectErrorInfo,
  RPCRequest,
  RPCResponse,
  SyncMessage,
  RPCRequestFromServer,
} from "./types";

// Use global WebSocket if available (Browser, Bun, Deno)
let GlobalWebSocket: any = undefined;
if (typeof globalThis !== "undefined" && (globalThis as any).WebSocket) {
  GlobalWebSocket = (globalThis as any).WebSocket;
}

// WebSocket.OPEN is always 1
const READY_STATE_OPEN = 1;
const TOKEN_REFRESH_LEEWAY_MS = 2 * 60 * 1000;
const TOKEN_REFRESH_RETRY_MS = 30 * 1000;
const MAX_TIMER_DELAY_MS = 2_147_483_647;
const DEBUG_WS = process.env.GRANULAR_DEBUG_WS === "1";
const DEFAULT_RPC_TIMEOUT_MS = 30_000;
const DOMAIN_PACKAGE_RPC_TIMEOUT_MS = 120_000;
const EFFECT_CONTROL_RPC_TIMEOUT_MS = 120_000;
const HARNESS_RUN_RPC_TIMEOUT_MS = 600_000;
const DEFAULT_RECONNECT_DELAY_MS = 3_000;
const DEFAULT_MAX_RECONNECT_ATTEMPTS = 5;

function debugWs(...args: unknown[]): void {
  if (DEBUG_WS) {
    console.log(...args);
  }
}

function rpcTimeoutMsForMethod(method: string): number {
  switch (method) {
    case "domain.fetchPackagePart":
    case "domain.getSummary":
      return DOMAIN_PACKAGE_RPC_TIMEOUT_MS;
    case "client.heartbeat":
    case "effects.publishCatalog":
    case "effects.refresh":
      return EFFECT_CONTROL_RPC_TIMEOUT_MS;
    case "harness.run":
      return HARNESS_RUN_RPC_TIMEOUT_MS;
    default:
      return DEFAULT_RPC_TIMEOUT_MS;
  }
}

export class WSClient {
  private ws: WebSocket | null = null;
  private url: string;
  private sessionId: string;
  private token: string;
  private messageQueue: Array<{
    resolve: (value: unknown) => void;
    reject: (error: Error) => void;
    id: string;
  }> = [];
  private syncHandlers: Array<(message: SyncMessage) => void> = [];
  private rpcHandlers: Map<string, (params: unknown) => Promise<unknown>> =
    new Map();
  private eventHandlers: Map<string, Array<(params: unknown) => void>> =
    new Map();
  private nextRpcId = 1;
  public doc: Automerge.Doc<Record<string, unknown>> = Automerge.init();
  private syncState: Automerge.SyncState = Automerge.initSyncState();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private isExplicitlyDisconnected = false;
  private reconnectAttempts = 0;

  private options: WSClientOptions;

  constructor(options: WSClientOptions) {
    this.options = options;
    this.url = options.url;
    this.sessionId = options.sessionId;
    this.token = options.token;
  }

  get currentSessionId(): string {
    return this.sessionId;
  }

  private clearTokenRefreshTimer(): void {
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  private decodeBase64Url(payload: string): string {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4 || 4)) % 4);
    if (typeof atob === "function") {
      return atob(padded);
    }

    const maybeBuffer = (
      globalThis as unknown as {
        Buffer?: {
          from: (
            data: string,
            encoding: string,
          ) => { toString: (encoding: string) => string };
        };
      }
    ).Buffer;
    if (maybeBuffer) {
      return maybeBuffer.from(padded, "base64").toString("utf8");
    }

    throw new Error("No base64 decoder available");
  }

  private getTokenExpiryMs(token: string): number | null {
    const parts = token.split(".");
    if (parts.length < 2) {
      return null;
    }

    try {
      const payloadSegment = parts[1];
      if (!payloadSegment) {
        return null;
      }
      const payloadRaw = this.decodeBase64Url(payloadSegment);
      const payload = JSON.parse(payloadRaw) as { exp?: number };
      if (typeof payload.exp !== "number" || !Number.isFinite(payload.exp)) {
        return null;
      }
      return payload.exp * 1000;
    } catch {
      return null;
    }
  }

  private scheduleTokenRefresh(): void {
    this.clearTokenRefreshTimer();
    if (!this.options.tokenProvider || this.isExplicitlyDisconnected) {
      return;
    }

    const expiresAt = this.getTokenExpiryMs(this.token);
    if (!expiresAt) {
      return;
    }

    const refreshInMs = Math.max(
      1_000,
      expiresAt - Date.now() - TOKEN_REFRESH_LEEWAY_MS,
    );
    const delay = Math.min(refreshInMs, MAX_TIMER_DELAY_MS);
    this.tokenRefreshTimer = setTimeout(() => {
      void this.refreshTokenInBackground();
    }, delay);
  }

  private async refreshTokenInBackground(): Promise<void> {
    if (!this.options.tokenProvider || this.isExplicitlyDisconnected) {
      return;
    }

    try {
      const refreshedToken = await this.options.tokenProvider();
      if (typeof refreshedToken !== "string" || refreshedToken.length === 0) {
        throw new Error("Token provider returned no token");
      }
      this.token = refreshedToken;
      this.scheduleTokenRefresh();
    } catch (error) {
      if (this.isExplicitlyDisconnected) {
        return;
      }
      console.warn("[Granular] Token refresh failed, retrying soon:", error);
      this.clearTokenRefreshTimer();
      this.tokenRefreshTimer = setTimeout(() => {
        void this.refreshTokenInBackground();
      }, TOKEN_REFRESH_RETRY_MS);
    }
  }

  private async resolveTokenForConnect(): Promise<string> {
    if (!this.options.tokenProvider) {
      return this.token;
    }

    const expiresAt = this.getTokenExpiryMs(this.token);
    const shouldRefresh =
      expiresAt !== null && expiresAt - Date.now() <= TOKEN_REFRESH_LEEWAY_MS;
    if (!shouldRefresh) {
      return this.token;
    }

    try {
      const refreshedToken = await this.options.tokenProvider();
      if (typeof refreshedToken !== "string" || refreshedToken.length === 0) {
        throw new Error("Token provider returned no token");
      }
      this.token = refreshedToken;
      return refreshedToken;
    } catch (error) {
      if (expiresAt > Date.now()) {
        console.warn(
          "[Granular] Token refresh failed, using current token:",
          error,
        );
        return this.token;
      }
      throw error;
    }
  }

  /**
   * Connect to the WebSocket server
   * @returns {Promise<void>} Resolves when connection is open
   */
  async connect(): Promise<void> {
    const token = await this.resolveTokenForConnect();
    this.isExplicitlyDisconnected = false;
    this.scheduleTokenRefresh();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Determine WebSocket implementation
    let WebSocketClass = this.options.WebSocketCtor || GlobalWebSocket;

    if (!WebSocketClass) {
      try {
        // Try to load 'ws' dynamically for Node.js environments
        // @ts-ignore
        const wsModule = await import("ws");
        WebSocketClass = wsModule.default || wsModule;
      } catch (e) {
        // Ignore, handled below
      }
    }

    if (!WebSocketClass) {
      throw new Error(
        'No WebSocket implementation found. If using Node.js, please install "ws" and pass the constructor to the SDK options: { WebSocketCtor: WebSocket }.',
      );
    }

    return new Promise((resolve, reject) => {
      try {
        // Construct URL with query parameters
        const wsUrl = new URL(this.url);
        wsUrl.searchParams.set("sessionId", this.sessionId);
        wsUrl.searchParams.set("token", token);

        // @ts-ignore - Dynamic class usage
        this.ws = new WebSocketClass(wsUrl.toString()) as WebSocket;

        if (!this.ws) throw new Error("Failed to create WebSocket");

        // Standardize event handlers
        const socket = this.ws as any;
        if (typeof socket.on === "function") {
          // Node 'ws' style
          socket.on("open", () => {
            if (this.reconnectTimer) {
              clearTimeout(this.reconnectTimer);
              this.reconnectTimer = null;
            }
            this.reconnectAttempts = 0;
            this.emit("open", {});
            resolve();
          });
          socket.on("message", (data: any) => {
            try {
              const message = JSON.parse(data.toString());
              this.handleMessage(message);
            } catch (error) {
              console.error("[Granular] Failed to parse message:", error);
            }
          });
          socket.on("error", (error: any) => {
            this.emit("error", error);
            if (socket.readyState !== READY_STATE_OPEN) {
              reject(error);
            }
          });
          socket.on("close", (code: number, reason: unknown) => {
            this.handleDisconnect({
              code,
              reason: this.normalizeReason(reason),
              // ws does not provide wasClean on Node-style close callback
              wasClean: code === 1000,
            });
          });
        } else {
          // Browser WebSocket style
          this.ws!.onopen = () => {
            if (this.reconnectTimer) {
              clearTimeout(this.reconnectTimer);
              this.reconnectTimer = null;
            }
            this.reconnectAttempts = 0;
            this.emit("open", {});
            resolve();
          };

          this.ws!.onmessage = (event: MessageEvent) => {
            try {
              const data = event.data;
              const message = JSON.parse(data.toString());
              this.handleMessage(message);
            } catch (error) {
              console.error("[Granular] Failed to parse message:", error);
            }
          };

          this.ws!.onerror = (event: Event) => {
            const error = new Error("WebSocket error");
            (error as any).event = event;
            this.emit("error", error);
            if (this.ws?.readyState !== READY_STATE_OPEN) {
              reject(error);
            }
          };

          this.ws!.onclose = (event: CloseEvent) => {
            this.handleDisconnect({
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean,
            });
          };
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  private normalizeReason(reason: unknown): string | undefined {
    if (reason === null || reason === undefined) return undefined;
    if (typeof reason === "string") return reason;
    if (typeof reason === "object" && reason && "toString" in reason) {
      try {
        const text = String((reason as any).toString());
        return text || undefined;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  private rejectPending(error: Error): void {
    this.messageQueue.forEach((pending) => pending.reject(error));
    this.messageQueue = [];
  }

  private buildDisconnectError(info: WSDisconnectInfo): Error {
    const details = [
      info.code !== undefined ? `code=${info.code}` : undefined,
      info.reason ? `reason=${info.reason}` : undefined,
    ]
      .filter(Boolean)
      .join(", ");
    const suffix = details ? ` (${details})` : "";
    return new Error(`WebSocket disconnected${suffix}`);
  }

  private handleDisconnect(
    close: {
      code?: number;
      reason?: string;
      wasClean?: boolean;
    } = {},
  ): void {
    const baseReconnectDelayMs =
      typeof this.options.reconnectDelayMs === "number" &&
      Number.isFinite(this.options.reconnectDelayMs) &&
      this.options.reconnectDelayMs > 0
        ? this.options.reconnectDelayMs
        : DEFAULT_RECONNECT_DELAY_MS;
    const maxReconnectAttempts =
      typeof this.options.maxReconnectAttempts === "number" &&
      Number.isFinite(this.options.maxReconnectAttempts) &&
      this.options.maxReconnectAttempts >= 0
        ? Math.floor(this.options.maxReconnectAttempts)
        : DEFAULT_MAX_RECONNECT_ATTEMPTS;
    const unexpected = !this.isExplicitlyDisconnected;
    const info: WSDisconnectInfo = {
      code: close.code,
      reason: close.reason,
      wasClean: close.wasClean,
      unexpected,
      timestamp: Date.now(),
      reconnectScheduled: false,
    };

    this.ws = null;
    this.emit("close", info);

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (unexpected) {
      const disconnectError = this.buildDisconnectError(info);
      this.rejectPending(disconnectError);
      this.emit("disconnect", info);

      if (this.reconnectAttempts >= maxReconnectAttempts) {
        const reconnectInfo: WSReconnectErrorInfo = {
          error: `WebSocket reconnect attempts exhausted after ${maxReconnectAttempts} attempt(s).`,
          sessionId: this.sessionId,
          timestamp: Date.now(),
        };
        this.emit("reconnect_error", reconnectInfo);
        if (this.options.onReconnectError) {
          try {
            this.options.onReconnectError(reconnectInfo);
          } catch (callbackError) {
            console.error(
              "[Granular] onReconnectError callback failed:",
              callbackError,
            );
          }
        }
        return;
      }

      this.reconnectAttempts += 1;
      const reconnectDelayMs = Math.min(
        30_000,
        baseReconnectDelayMs * 2 ** Math.max(0, this.reconnectAttempts - 1),
      );
      info.reconnectScheduled = true;
      info.reconnectDelayMs = reconnectDelayMs;

      if (this.options.onUnexpectedClose) {
        try {
          this.options.onUnexpectedClose(info);
        } catch (callbackError) {
          console.error(
            "[Granular] onUnexpectedClose callback failed:",
            callbackError,
          );
        }
      }

      this.reconnectTimer = setTimeout(() => {
        console.log("[Granular] Attempting reconnect...");
        this.connect().catch((error) => {
          console.error("[Granular] Reconnect failed:", error);
          const reconnectInfo: WSReconnectErrorInfo = {
            error: error instanceof Error ? error.message : String(error),
            sessionId: this.sessionId,
            timestamp: Date.now(),
          };
          this.emit("reconnect_error", reconnectInfo);
          if (this.options.onReconnectError) {
            try {
              this.options.onReconnectError(reconnectInfo);
            } catch (callbackError) {
              console.error(
                "[Granular] onReconnectError callback failed:",
                callbackError,
              );
            }
          }
        });
      }, reconnectDelayMs);
    }
  }

  private handleMessage(message: unknown): void {
    if (typeof message !== "object" || message === null) return;

    // Debug: Log all incoming messages
    debugWs(
      "[Granular DEBUG] Received message:",
      JSON.stringify(message).slice(0, 500),
    );

    // 1. Handle Sync Messages (Automerge)
    if ("type" in message && message.type === "sync") {
      const syncMessage = message as SyncMessage;
      let bytes: Uint8Array;

      try {
        const payload = syncMessage.message || syncMessage.data;

        if (typeof payload === "string") {
          const binaryString = atob(payload);
          const len = binaryString.length;
          bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
        } else if (Array.isArray(payload)) {
          bytes = new Uint8Array(payload);
        } else if (payload instanceof Uint8Array) {
          bytes = payload;
        } else {
          return;
        }

        debugWs("[Granular DEBUG] Applying sync bytes:", bytes.length);

        const [newDoc, newSyncState] = Automerge.receiveSyncMessage(
          this.doc,
          this.syncState,
          bytes,
        );
        this.doc = newDoc;
        this.syncState = newSyncState;

        // DEBUG: Inspect doc after sync
        const docAny = this.doc as any;
        if (docAny.catalog) {
          debugWs(
            "[Granular DEBUG] Doc catalog sync applied. Keys in catalog:",
            Object.keys(docAny.catalog || {}),
          );
          debugWs(
            "[Granular DEBUG] RawToolCatalogs:",
            Object.keys(docAny.catalog.rawToolCatalogs || {}),
          );
        } else {
          debugWs(
            "[Granular DEBUG] Doc synced but no catalog yet. Keys in doc:",
            Object.keys(docAny),
          );
        }

        // Notify sync listeners with the new doc
        this.emit("sync", this.doc);
      } catch (e) {
        // Determine if this is a sync protocol error (e.g. wrong format) -> try applyChanges
        // The server broadcasts raw changes using getLastLocalChange, which receiveSyncMessage rejects.
        try {
          debugWs(
            "[Granular DEBUG] receiveSyncMessage failed, trying applyChanges...",
          );
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const [newDoc] = Automerge.applyChanges(this.doc, [bytes!]);
          this.doc = newDoc;
          this.emit("sync", this.doc);
          debugWs(
            "[Granular DEBUG] applyChanges succeeded. Doc:",
            JSON.stringify(Automerge.toJS(this.doc)),
          );
        } catch (applyError) {
          console.warn(
            "[Granular] Failed to apply sync message (both sync & applyChanges)",
            e,
            applyError,
          );
        }
      }
      return;
    }

    // 1. Handling Automerge Snapshots (Full State)
    if ("type" in message && message.type === "snapshot") {
      const snapshotMessage = message as { type: string; data: number[] };
      try {
        const bytes = new Uint8Array(snapshotMessage.data);
        debugWs(
          "[Granular DEBUG] Loading Automerge session snapshot bytes:",
          bytes.length,
        );
        this.doc = Automerge.load(bytes);
        this.emit("sync", this.doc);
        debugWs(
          "[Granular DEBUG] Automerge session snapshot loaded. Doc:",
          JSON.stringify(Automerge.toJS(this.doc)),
        );
      } catch (e) {
        console.warn("[Granular] Failed to load snapshot message", e);
      }
      return;
    }

    // 1.5 Handle Automerge Changes (Raw)
    if ("type" in message && message.type === "change") {
      const changeMessage = message as { type: string; data: number[] };
      try {
        const bytes = new Uint8Array(changeMessage.data);
        // console.log('[Granular DEBUG] Applying change bytes:', bytes.length);
        const [newDoc] = Automerge.applyChanges(this.doc, [bytes]);
        this.doc = newDoc;
        this.emit("change", changeMessage);
        this.emit("sync", this.doc);
        // console.log('[Granular DEBUG] Valid change applied. Doc keys:', Object.keys(Automerge.toJS(this.doc)));
      } catch (e) {
        console.warn("[Granular] Failed to apply change message", e);
      }
      return;
    }

    // 2. Handle RPC Responses (Results/Errors from calls we made)
    if (
      "type" in message &&
      (message.type === "rpc_result" || message.type === "rpc_error")
    ) {
      const response = message as RPCResponse;
      const pending = this.messageQueue.find((q) => q.id === response.id);
      if (pending) {
        if (response.type === "rpc_error") {
          pending.reject(
            new Error(
              `RPC error: ${response.error?.message || "Unknown error"}`,
            ),
          );
        } else {
          pending.resolve(response.result);
        }
        this.messageQueue = this.messageQueue.filter(
          (q) => q.id !== response.id,
        );
      }
      return;
    }

    // 3. Handle Incoming RPC Requests (Server calls us, e.g. effect.invoke)
    if (
      "type" in message &&
      message.type === "rpc" &&
      "method" in message &&
      "id" in message
    ) {
      const request = message as RPCRequestFromServer;
      this.handleIncomingRpc(request).catch((error) => {
        console.error("[Granular] Error handling incoming RPC:", error);
      });
      return;
    }

    // 4. Handle Generic Events
    if ("type" in message && typeof message.type === "string") {
      this.emit(message.type, message);
    }
  }

  /**
   * Make an RPC call to the server
   * @param {string} method - RPC method name
   * @param {unknown} params - Request parameters
   * @returns {Promise<unknown>} Response result
   * @throws {Error} If connection is closed or timeout occurs
   */
  async call(method: string, params: unknown): Promise<unknown> {
    if (!this.ws || this.ws.readyState !== READY_STATE_OPEN) {
      throw new Error("WebSocket not connected");
    }

    const id = `rpc-${this.nextRpcId++}`;
    const request: RPCRequest = {
      type: "rpc",
      method,
      params,
      id,
    };

    return new Promise((resolve, reject) => {
      this.messageQueue.push({ resolve, reject, id });

      this.ws!.send(JSON.stringify(request));

      const timeoutMs = rpcTimeoutMsForMethod(method);
      setTimeout(() => {
        const pending = this.messageQueue.find((q) => q.id === id);
        if (pending) {
          this.messageQueue = this.messageQueue.filter((q) => q.id !== id);
          reject(new Error(`RPC timeout: ${method}`));
        }
      }, timeoutMs);
    });
  }

  private async handleIncomingRpc(
    request: RPCRequestFromServer,
  ): Promise<void> {
    const handler = this.rpcHandlers.get(request.method);

    if (!handler) {
      const errorResponse = {
        type: "rpc_error",
        id: request.id,
        error: {
          code: -32601,
          message: `Method not found: ${request.method}`,
        },
      };
      this.ws?.send(JSON.stringify(errorResponse));
      return;
    }

    try {
      const result = await handler(request.params);

      // Reverse effect invocations usually resolve by returning a value directly.
      // Only tool-era compatibility flows manage their own response frames.
      if (request.method !== "tool.invoke") {
        const successResponse = {
          type: "rpc_result",
          id: request.id,
          result,
        };
        this.ws?.send(JSON.stringify(successResponse));
      }
    } catch (error) {
      const errorResponse = {
        type: "rpc_error",
        id: request.id,
        error: {
          code: -32000,
          message: error instanceof Error ? error.message : "Unknown error",
        },
      };
      this.ws?.send(JSON.stringify(errorResponse));
    }
  }

  /**
   * Subscribe to client events
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   */
  on(event: string, handler: (params: unknown) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler);
  }

  /**
   * Register an RPC handler for incoming server requests
   * @param {string} method - RPC method name
   * @param {Function} handler - Handler function
   */
  registerRpcHandler(
    method: string,
    handler: (params: unknown) => Promise<unknown>,
  ): void {
    this.rpcHandlers.set(method, handler);
  }

  /**
   * Unsubscribe from client events
   * @param {string} event - Event name
   * @param {Function} handler - Handler to remove
   */
  off(event: string, handler: (params: unknown) => void): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      this.eventHandlers.set(
        event,
        handlers.filter((h) => h !== handler),
      );
    }
  }

  /**
   * Emit an event locally
   * @param {string} event - Event name
   * @param params - Event data
   */
  emit(event: string, params: unknown): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => handler(params));
    }
  }

  /**
   * Disconnect the WebSocket and clear state
   */
  disconnect(): void {
    this.isExplicitlyDisconnected = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearTokenRefreshTimer();

    if (this.ws) {
      this.ws.close(1000, "Client disconnect");
      this.ws = null;
    }
    this.rejectPending(new Error("Client explicitly disconnected"));
    this.rpcHandlers.clear();
    this.emit("disconnect", {});
  }
}
